import React, { useState, useEffect } from 'react';
import useUsers from '../hooks/useUsers';
import { sendAnnouncementEmail } from '../utils/emailService';
import toast from 'react-hot-toast';

const EmailTab = () => {
  const { students, loading: usersLoading } = useUsers();
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [emailData, setEmailData] = useState({
    subject: '',
    content: '',
    priority: 'medium',
    targetAudience: 'students'
  });
  const [sending, setSending] = useState(false);
  const [emailHistory, setEmailHistory] = useState([]);

  // Load email history from localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem('emailHistory');
    if (savedHistory) {
      try {
        setEmailHistory(JSON.parse(savedHistory));
      } catch (error) {
        console.error('Error loading email history:', error);
      }
    }
  }, []);

  // Save email history to localStorage
  const saveEmailHistory = (emailRecord) => {
    const newHistory = [emailRecord, ...emailHistory];
    setEmailHistory(newHistory);
    localStorage.setItem('emailHistory', JSON.stringify(newHistory));
  };

  const handleStudentSelection = (studentId, isSelected) => {
    if (isSelected) {
      const student = students.find(s => s.id === studentId);
      if (student && student.email) {
        setSelectedStudents(prev => [...prev, {
          id: studentId,
          email: student.email,
          name: student.displayName || student.name || 'Student'
        }]);
      }
    } else {
      setSelectedStudents(prev => prev.filter(s => s.id !== studentId));
    }
  };

  const handleSelectAll = () => {
    const studentsWithEmails = students.filter(s => s.email);
    setSelectedStudents(studentsWithEmails.map(s => ({
      id: s.id,
      email: s.email,
      name: s.displayName || s.name || 'Student'
    })));
  };

  const handleDeselectAll = () => {
    setSelectedStudents([]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEmailData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSendEmail = async () => {
    if (!emailData.subject.trim() || !emailData.content.trim()) {
      toast.error('Please fill in both subject and content');
      return;
    }

    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }

    setSending(true);
    try {
      const announcementData = {
        title: emailData.subject,
        content: emailData.content,
        priority: emailData.priority,
        targetAudience: emailData.targetAudience,
        createdAt: new Date(),
        expiresAt: null,
        isActive: true
      };

      const customRecipients = selectedStudents.map(s => s.email);
      const result = await sendAnnouncementEmail(announcementData, customRecipients);

      // Save to history
      const emailRecord = {
        id: Date.now().toString(),
        subject: emailData.subject,
        content: emailData.content,
        priority: emailData.priority,
        recipients: selectedStudents.length,
        successfulSends: result.successfulSends,
        failedSends: result.failedSends,
        sentAt: new Date().toISOString(),
        recipientEmails: customRecipients
      };
      saveEmailHistory(emailRecord);

      toast.success(`Email sent successfully! ${result.successfulSends} delivered, ${result.failedSends} failed`);
      
      // Reset form
      setEmailData({
        subject: '',
        content: '',
        priority: 'medium',
        targetAudience: 'students'
      });
      setSelectedStudents([]);
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  const handleClearHistory = () => {
    setEmailHistory([]);
    localStorage.removeItem('emailHistory');
    toast.success('Email history cleared');
  };

  if (usersLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Email Composition Form */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Compose Email</h3>
        
        <div className="space-y-4">
          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subject *
            </label>
            <input
              type="text"
              name="subject"
              value={emailData.subject}
              onChange={handleInputChange}
              placeholder="Enter email subject"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Priority
            </label>
            <select
              name="priority"
              value={emailData.priority}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content *
            </label>
            <textarea
              name="content"
              value={emailData.content}
              onChange={handleInputChange}
              placeholder="Enter email content"
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Student Selection */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Select Recipients ({selectedStudents.length} selected)
          </h3>
          <div className="space-x-2">
            <button
              onClick={handleSelectAll}
              className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
            >
              Select All
            </button>
            <button
              onClick={handleDeselectAll}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Deselect All
            </button>
          </div>
        </div>

        <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
          {students.filter(s => s.email).map((student) => (
            <div
              key={student.id}
              className="flex items-center p-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
            >
              <input
                type="checkbox"
                id={`student-${student.id}`}
                checked={selectedStudents.some(s => s.id === student.id)}
                onChange={(e) => handleStudentSelection(student.id, e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label
                htmlFor={`student-${student.id}`}
                className="ml-3 flex-1 cursor-pointer"
              >
                <div className="font-medium text-gray-900">
                  {student.displayName || student.name || 'Student'}
                </div>
                <div className="text-sm text-gray-500">{student.email}</div>
              </label>
            </div>
          ))}
        </div>

        {students.filter(s => s.email).length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No students with email addresses found
          </div>
        )}
      </div>

      {/* Send Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSendEmail}
          disabled={sending || selectedStudents.length === 0 || !emailData.subject.trim() || !emailData.content.trim()}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          {sending && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          )}
          <span>{sending ? 'Sending...' : 'Send Email'}</span>
        </button>
      </div>

      {/* Email History */}
      {emailHistory.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Email History</h3>
            <button
              onClick={handleClearHistory}
              className="px-3 py-1 text-sm text-red-600 hover:text-red-700"
            >
              Clear History
            </button>
          </div>

          <div className="space-y-3">
            {emailHistory.slice(0, 10).map((email) => (
              <div
                key={email.id}
                className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{email.subject}</h4>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{email.content}</p>
                    <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                      <span>Priority: {email.priority}</span>
                      <span>Recipients: {email.recipients}</span>
                      <span>Sent: {new Date(email.sentAt).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="text-right text-sm">
                    <div className="text-green-600">✓ {email.successfulSends} sent</div>
                    {email.failedSends > 0 && (
                      <div className="text-red-600">✗ {email.failedSends} failed</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTab;
